/**
 * Inline quiz builder for a course subtopic (Node editor → Content type → Quiz).
 *
 *   <QuizBuilder api courseId nodeId categoryId value={quiz} onChange={setQuiz} />
 *   quiz = { questions: [questionDoc…], settings: { time_limit_minutes, passing_score, max_attempts, shuffle_questions } }
 *
 * Questions are real question-bank records (created the moment they are added — manually, from a
 * .docx / .csv / .json import, or picked from the bank); the node editor turns them into a quiz
 * (exam) when the node is saved.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FormSection } from '../shell/RecordPage';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { stripHtml } from '../richtext/RichText';
import { typeLabel } from '../questions/QuestionPreview';
import { Plus, Trash, FileArrowUp, ListChecks, MagnifyingGlass, Check, X, DownloadSimple, ArrowUp, ArrowDown, Exam } from '@phosphor-icons/react';

const TYPES = [
  { value: 'MCQ_SINGLE', label: 'Multiple choice (one answer)' },
  { value: 'MCQ_MULTI', label: 'Multiple choice (several answers)' },
  { value: 'TRUE_FALSE', label: 'True / False' },
  { value: 'SHORT_ANSWER', label: 'Short answer' },
  { value: 'NUMERIC', label: 'Numeric' },
];
const EMPTY_Q = { question_text: '', question_type: 'MCQ_SINGLE', options: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }], correct_answer: '', numeric_answer: '', points: 1, difficulty: 'medium', explanation: '' };

export const DEFAULT_QUIZ_SETTINGS = { time_limit_minutes: '', passing_score: 60, max_attempts: 1, shuffle_questions: false };

function QuestionComposer({ onAdd, onCancel, saving }) {
  const [q, setQ] = useState({ ...EMPTY_Q, options: EMPTY_Q.options.map((o) => ({ ...o })) });
  const isMcq = q.question_type === 'MCQ_SINGLE' || q.question_type === 'MCQ_MULTI';
  const setOpt = (i, patch) => setQ((p) => ({ ...p, options: p.options.map((o, j) => (j === i ? { ...o, ...patch } : (patch.is_correct && p.question_type === 'MCQ_SINGLE' ? { ...o, is_correct: false } : o))) }));
  const submit = (e) => {
    e.preventDefault();
    if (!q.question_text.trim()) { toast.error('Enter the question text'); return; }
    const payload = { question_text: q.question_text.trim(), question_type: q.question_type, points: Number(q.points) || 1, difficulty: q.difficulty, explanation: q.explanation || null, options: [] };
    if (isMcq) {
      payload.options = q.options.filter((o) => o.text.trim()).map((o) => ({ text: o.text.trim(), is_correct: !!o.is_correct }));
      if (payload.options.length < 2) { toast.error('Give at least two options'); return; }
      if (!payload.options.some((o) => o.is_correct)) { toast.error('Mark the correct option'); return; }
    } else if (q.question_type === 'TRUE_FALSE') {
      payload.options = [{ text: 'True', is_correct: q.correct_answer !== 'false' }, { text: 'False', is_correct: q.correct_answer === 'false' }];
    } else if (q.question_type === 'SHORT_ANSWER') {
      if (!q.correct_answer.trim()) { toast.error('Enter the expected answer'); return; }
      payload.correct_answer = q.correct_answer.trim();
    } else if (q.question_type === 'NUMERIC') {
      if (q.numeric_answer === '' || Number.isNaN(Number(q.numeric_answer))) { toast.error('Enter the numeric answer'); return; }
      payload.numeric_answer = Number(q.numeric_answer);
    }
    onAdd(payload);
  };
  return (
    <form onSubmit={submit} className="rounded border border-shell-primary/30 bg-shell-primary/5 p-3 space-y-3" data-testid="quiz-question-composer">
      <div className="form-grid">
        <div className="field sm:col-span-2 field-wide">
          <Label>Question *</Label>
          <Textarea value={q.question_text} onChange={(e) => setQ({ ...q, question_text: e.target.value })} rows={2} placeholder="Type the question…" autoFocus data-testid="qc-text" />
        </div>
        <div className="field">
          <Label>Type</Label>
          <select className="form-select" value={q.question_type} onChange={(e) => setQ({ ...q, question_type: e.target.value, correct_answer: '' })} data-testid="qc-type">
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="field">
          <Label>Marks</Label>
          <Input type="number" min="0" step="0.5" value={q.points} onChange={(e) => setQ({ ...q, points: e.target.value })} data-testid="qc-points" />
        </div>
      </div>
      {isMcq && (
        <div className="space-y-1.5">
          <Label>Options <span className="field-hint">— tick the correct one{q.question_type === 'MCQ_MULTI' ? 's' : ''}</span></Label>
          {q.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type={q.question_type === 'MCQ_SINGLE' ? 'radio' : 'checkbox'} name="qc-correct" checked={!!o.is_correct} onChange={(e) => setOpt(i, { is_correct: q.question_type === 'MCQ_SINGLE' ? true : e.target.checked })} title="Correct" data-testid={`qc-correct-${i}`} />
              <Input value={o.text} onChange={(e) => setOpt(i, { text: e.target.value })} placeholder={`Option ${String.fromCharCode(65 + i)}`} className="flex-1" data-testid={`qc-option-${i}`} />
              {q.options.length > 2 && <button type="button" className="p-1 text-muted-foreground hover:text-destructive" onClick={() => setQ({ ...q, options: q.options.filter((_, j) => j !== i) })} title="Remove option"><X className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
          {q.options.length < 8 && <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => setQ({ ...q, options: [...q.options, { text: '', is_correct: false }] })}><Plus className="w-3.5 h-3.5" /> Option</Button>}
        </div>
      )}
      {q.question_type === 'TRUE_FALSE' && (
        <div className="field"><Label>Correct answer</Label>
          <select className="form-select" value={q.correct_answer || 'true'} onChange={(e) => setQ({ ...q, correct_answer: e.target.value })}><option value="true">True</option><option value="false">False</option></select>
        </div>
      )}
      {q.question_type === 'SHORT_ANSWER' && <div className="field"><Label>Expected answer</Label><Input value={q.correct_answer} onChange={(e) => setQ({ ...q, correct_answer: e.target.value })} data-testid="qc-answer" /></div>}
      {q.question_type === 'NUMERIC' && <div className="field"><Label>Numeric answer</Label><Input type="number" step="any" value={q.numeric_answer} onChange={(e) => setQ({ ...q, numeric_answer: e.target.value })} data-testid="qc-answer" /></div>}
      <div className="field field-wide"><Label>Explanation <span className="field-hint">(optional, shown after answering)</span></Label><Textarea value={q.explanation} onChange={(e) => setQ({ ...q, explanation: e.target.value })} rows={2} /></div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" className="h-8" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" className="h-8" disabled={saving} data-testid="qc-add"><Check className="w-4 h-4" /> Add question</Button>
      </div>
    </form>
  );
}

function BankPicker({ api, exclude, onPick, onClose }) {
  const [all, setAll] = useState([]);
  const [query, setQuery] = useState('');
  useEffect(() => { api.get('/question-bank?limit=2000').then((r) => setAll(r.data || [])).catch(() => setAll([])); }, [api]);
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((x) => !exclude.has(x.id) && (!q || stripHtml(x.question_text).toLowerCase().includes(q))).slice(0, 50);
  }, [all, query, exclude]);
  return (
    <div className="rounded border border-border p-3 space-y-2" data-testid="quiz-bank-picker">
      <div className="flex items-center gap-2">
        <div className="relative flex-1"><MagnifyingGlass className="w-4 h-4 absolute left-2 top-2 text-muted-foreground" /><Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the question bank…" className="pl-7" autoFocus data-testid="bank-picker-search" /></div>
        <Button type="button" variant="ghost" size="sm" className="h-8" onClick={onClose}>Done</Button>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-border">
        {list.length === 0 && <p className="text-[13px] text-muted-foreground py-3 text-center">No matching questions.</p>}
        {list.map((x) => (
          <button type="button" key={x.id} onClick={() => onPick(x)} className="w-full text-left flex items-center gap-2 py-1.5 px-1 hover:bg-muted/60 text-[13px]" data-testid={`bank-pick-${x.id}`}>
            <Plus className="w-3.5 h-3.5 text-shell-primary flex-shrink-0" /><span className="flex-1 truncate">{stripHtml(x.question_text)}</span><span className="text-[11px] text-muted-foreground">{typeLabel(x.question_type)} · {x.points ?? 1}m</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function QuizBuilder({ api, courseId, nodeId, categoryId, bankNodeId, value, onChange }) {
  const fileNode = nodeId || bankNodeId || null; // questions are filed under this chapter / topic
  const quiz = value || { questions: [], settings: DEFAULT_QUIZ_SETTINGS };
  const [mode, setMode] = useState(null); // 'compose' | 'import' | 'bank'
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const ids = useMemo(() => new Set(quiz.questions.map((q) => q.id)), [quiz.questions]);
  const setQuestions = (questions) => onChange({ ...quiz, questions });
  const setSetting = (k, v) => onChange({ ...quiz, settings: { ...quiz.settings, [k]: v } });

  const addManual = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post('/question-bank', { ...payload, course_id: courseId, node_id: fileNode, category_id: categoryId || null });
      setQuestions([...quiz.questions, r.data]);
      toast.success('Question added');
      setMode('compose');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add the question'); }
    finally { setBusy(false); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('course_id', courseId || ''); fd.append('category_id', categoryId || ''); if (fileNode) fd.append('node_id', fileNode);
      const r = await api.post('/question-bank/bulk-upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const created = await Promise.all((r.data.ids || []).map((id) => api.get(`/question-bank/${id}`).then((x) => x.data).catch(() => null)));
      setQuestions([...quiz.questions, ...created.filter(Boolean)]);
      setImportResult(r.data);
      toast.success(`${r.data.created} question${r.data.created === 1 ? '' : 's'} imported`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const template = async (fmt) => {
    try {
      const r = await api.get(`/question-bank/templates/${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `question-template.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the template'); }
  };

  const move = (i, d) => { const arr = [...quiz.questions]; const j = i + d; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; setQuestions(arr); };
  const totalMarks = quiz.questions.reduce((s, q) => s + (Number(q.points) || 1), 0);

  return (
    <>
      <FormSection title="Quiz questions" icon={Exam} bodyClassName="p-3 space-y-3" description={quiz.questions.length ? `${quiz.questions.length} question${quiz.questions.length === 1 ? '' : 's'} · ${totalMarks} marks` : 'Add questions by hand, import a Word / CSV / JSON file, or pick from the question bank'}>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" className="h-8" variant={mode === 'compose' ? 'default' : 'outline'} onClick={() => setMode(mode === 'compose' ? null : 'compose')} data-testid="quiz-add-manual"><Plus className="w-4 h-4" /> Add question</Button>
          <Button type="button" size="sm" className="h-8" variant={mode === 'import' ? 'default' : 'outline'} onClick={() => setMode(mode === 'import' ? null : 'import')} data-testid="quiz-import"><FileArrowUp className="w-4 h-4" /> Import file (.docx / .csv / .json)</Button>
          <Button type="button" size="sm" className="h-8" variant={mode === 'bank' ? 'default' : 'outline'} onClick={() => setMode(mode === 'bank' ? null : 'bank')} data-testid="quiz-from-bank"><ListChecks className="w-4 h-4" /> From question bank</Button>
        </div>

        {mode === 'compose' && <QuestionComposer onAdd={addManual} onCancel={() => setMode(null)} saving={busy} />}
        {mode === 'import' && (
          <div className="rounded border border-border p-3 space-y-2" data-testid="quiz-import-box">
            <p className="text-[13px]">Use the same file formats as the Question bank bulk upload — one table per question in Word. Templates:
              {['docx', 'csv', 'json'].map((f) => <button key={f} type="button" className="ml-2 inline-flex items-center gap-1 text-shell-primary underline" onClick={() => template(f)}><DownloadSimple className="w-3.5 h-3.5" />{f}</button>)}
            </p>
            <input ref={fileRef} type="file" accept=".docx,.csv,.json" onChange={(e) => importFile(e.target.files?.[0])} disabled={busy} className="text-[13px]" data-testid="quiz-import-file" />
            {busy && <p className="field-hint">Importing…</p>}
            {importResult && <p className="field-hint">{importResult.created} imported{importResult.failed ? `, ${importResult.failed} failed: ${importResult.errors.map((e) => `row ${e.row} — ${e.error}`).join('; ')}` : ''}</p>}
          </div>
        )}
        {mode === 'bank' && <BankPicker api={api} exclude={ids} onPick={(x) => setQuestions([...quiz.questions, x])} onClose={() => setMode(null)} />}

        {quiz.questions.length === 0 ? (
          <p className="text-[13px] text-muted-foreground py-4 text-center border border-dashed border-border rounded" data-testid="quiz-empty">No questions yet.</p>
        ) : (
          <ol className="divide-y divide-border rounded border border-border" data-testid="quiz-question-list">
            {quiz.questions.map((q, i) => (
              <li key={q.id} className="flex items-center gap-2 px-2 py-1.5 text-[13px]" data-testid={`quiz-q-${q.id}`}>
                <span className="w-6 text-right text-muted-foreground tabular-nums">{i + 1}.</span>
                <span className="flex-1 truncate" title={stripHtml(q.question_text)}>{stripHtml(q.question_text)}</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{typeLabel(q.question_type)} · {q.points ?? 1} mark{(q.points ?? 1) === 1 ? '' : 's'}</span>
                <button type="button" className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30" onClick={() => move(i, 1)} disabled={i === quiz.questions.length - 1} title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" className="p-1 text-muted-foreground hover:text-destructive" onClick={() => setQuestions(quiz.questions.filter((x) => x.id !== q.id))} title="Remove from quiz (stays in the question bank)" data-testid={`quiz-remove-${q.id}`}><Trash className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ol>
        )}
      </FormSection>

      <FormSection title="Quiz rules" icon={ListChecks} bodyClassName="p-3">
        <div className="form-grid">
          <div className="field"><Label>Time limit (min)</Label><Input type="number" min="0" value={quiz.settings.time_limit_minutes ?? ''} onChange={(e) => setSetting('time_limit_minutes', e.target.value)} placeholder="No limit" data-testid="quiz-time-limit" /></div>
          <div className="field"><Label>Pass mark (%)</Label><Input type="number" min="0" max="100" value={quiz.settings.passing_score} onChange={(e) => setSetting('passing_score', e.target.value)} data-testid="quiz-pass-mark" /></div>
          <div className="field"><Label>Attempts allowed</Label><Input type="number" min="1" value={quiz.settings.max_attempts} onChange={(e) => setSetting('max_attempts', e.target.value)} data-testid="quiz-attempts" /></div>
          <div className="field"><Label>Shuffle questions</Label><label className="h-8 inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!quiz.settings.shuffle_questions} onChange={(e) => setSetting('shuffle_questions', e.target.checked)} /> Different order per learner</label></div>
        </div>
      </FormSection>
    </>
  );
}
